/**
 * Utilities for handling UTC to local timezone conversion
 * Backend always returns dates in UTC ISO 8601 format with 'Z' suffix
 * These utilities ensure proper conversion to user's local timezone
 */

/**
 * Converts a UTC ISO 8601 string to a JavaScript Date object
 * Properly handles timezone interpretation
 * 
 * @param isoString - ISO 8601 string from backend (e.g., "2026-02-21T21:00:00.000Z")
 * @returns Date object in user's local timezone
 */
export const parseUTCDate = (isoString: string | undefined): Date | null => {
  if (!isoString) return null;
  
  try {
    // JavaScript's Date constructor correctly interprets ISO strings with 'Z' as UTC
    const date = new Date(isoString);
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${isoString}`);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error(`Error parsing date: ${isoString}`, error);
    return null;
  }
};

/**
 * Formats a UTC date string to local datetime string
 * Uses the browser's locale for formatting
 * 
 * @param isoString - ISO 8601 string from backend
 * @param options - Intl.DateTimeFormatOptions for customizing the format
 * @returns Formatted date string in user's local timezone
 */
export const formatUTCToLocal = (
  isoString: string | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  const date = parseUTCDate(isoString);
  
  if (!date) return '';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };
  
  try {
    return date.toLocaleString(
      navigator.language || 'pt-BR',
      options || defaultOptions
    );
  } catch (error) {
    console.error(`Error formatting date: ${isoString}`, error);
    return '';
  }
};

/**
 * Formats a UTC date string to local date only (without time)
 * 
 * @param isoString - ISO 8601 string from backend
 * @returns Formatted date string in user's local timezone
 */
export const formatUTCToLocalDate = (isoString: string | undefined): string => {
  return formatUTCToLocal(isoString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Formats a UTC date string to local time only
 * 
 * @param isoString - ISO 8601 string from backend
 * @returns Formatted time string in user's local timezone
 */
export const formatUTCToLocalTime = (isoString: string | undefined): string => {
  return formatUTCToLocal(isoString, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Formats a UTC date string to ISO format in local timezone
 * Useful for form inputs that expect ISO date format
 * 
 * @param isoString - ISO 8601 string from backend
 * @returns ISO date string in local timezone (YYYY-MM-DD)
 */
export const formatUTCToLocalISODate = (isoString: string | undefined): string => {
  const date = parseUTCDate(isoString);
  
  if (!date) return '';
  
  // Create a date string in local timezone (not UTC)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Formats a UTC date string to ISO format datetime in local timezone
 * 
 * @param isoString - ISO 8601 string from backend
 * @returns ISO datetime string in local timezone (YYYY-MM-DDTHH:mm:ss)
 */
export const formatUTCToLocalISO = (isoString: string | undefined): string => {
  const date = parseUTCDate(isoString);
  
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Converts a date-only string (YYYY-MM-DD) into a UTC ISO string using a fixed
 * time in Brazilian time (BRT, UTC-3). This preserves a stable time component
 * while allowing users to edit only the date.
 *
 * @param dateOnly - Date string (YYYY-MM-DD)
 * @param hourBrt - Hour in BRT to apply (default 8)
 * @param minute - Minute (default 0)
 * @param second - Second (default 0)
 * @returns UTC ISO string (e.g., 2026-02-21T11:00:00.000Z for 08:00 BRT)
 */
export const formatDateOnlyToUtcWithBrtTime = (
  dateOnly: string | undefined,
  hourBrt: number = 8,
  minute: number = 0,
  second: number = 0
): string => {
  if (!dateOnly) return '';

  const [year, month, day] = dateOnly.split('-').map(Number);

  if (!year || !month || !day) {
    console.error(`Invalid date-only string: ${dateOnly}`);
    return '';
  }

  const brtOffsetHours = -3;
  const utcHour = hourBrt - brtOffsetHours;
  const utcDate = new Date(Date.UTC(year, month - 1, day, utcHour, minute, second));

  return utcDate.toISOString();
};

/**
 * Gets timezone information
 * 
 * @returns Timezone offset in hours
 */
export const getTimezoneOffset = (): number => {
  return -new Date().getTimezoneOffset() / 60;
};

/**
 * Gets timezone name
 * 
 * @returns Timezone abbreviation (e.g., "BRT", "BRST")
 */
export const getTimezoneName = (): string => {
  return new Date().toLocaleTimeString('pt-BR', { timeZoneName: 'short' }).split(' ').pop() || '';
};
